<template>
  <div>
    <v-card flat class="ma-2 pa-2 card-rounded">
      <v-card-title>
        <h3 class="primary--text">Estágios</h3>
      </v-card-title>
      <v-divider />
      <v-data-table
        v-model="selected"
        :loading="isFetching"
        :headers="[...headers, { text: $t('common.Edit'), value: 'edit' }]"
        :items="formattedInternships || []"
        :pagination.sync="pagination"
        select-all
        item-key="id"
        class="elevation-0 text-truncate"
      >
        <template v-slot:headers="props">
          <tr>
            <th>
              <v-checkbox
                :input-value="props.all"
                :indeterminate="props.indeterminate"
                primary
                hide-details
                @click.stop="toggleAll"
              />
            </th>
            <th
              v-for="header in props.headers"
              :key="header.text"
              :class="[
                'column sortable',
                pagination.descending ? 'desc' : 'asc',
                header.value === pagination.sortBy ? 'active' : ''
              ]"
              @click="changeSort(header.value)"
            >
              <v-icon small>arrow_upward</v-icon>
              {{ header.text }}
            </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <tr :active="props.selected">
            <td>
              <v-checkbox v-model="props.selected" primary hide-details />
            </td>
            <td
              class="text-xs-center"
              @click="editInternship(props)"
              v-for="header in headers"
              :key="header.value"
            >
              {{ props.item[header.value] }}
            </td>
            <td style="text-align: center">
              <v-btn icon @click.stop="() => editInternship(props)">
                <Icon name="edit" size="16px" />
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <SlotDial>
      <v-btn fab dark small color="indigo" @click="dialog = true">
        <v-icon>add</v-icon>
      </v-btn>
      <v-btn fab dark small color="error" @click="deleteEntries">
        <v-icon>delete</v-icon>
      </v-btn>
    </SlotDial>
    <v-dialog
      v-model="dialog"
      persistent
      max-width="600"
      lazy
      :fullscreen="isMobile"
    >
      <internship-form
        :close-me="closeDialog"
        :currentInternshipData="selectedInternship"
        :key="(selectedInternship || {}).id"
      />
    </v-dialog>
  </div>
</template>

<script>
/* eslint-disable vue/no-side-effects-in-computed-properties */
import SlotDial from '@/components/SlotDial'
import InternshipForm from './InternshipForm'
import cms from '@/content'
import { mapGetters, mapActions } from 'vuex'
import { HealthCenterGroup, Hospital } from '../../models/Content'
import { Internship, Option } from '../../models/Specialty'
import specialtyFetcher from '../../mixins/specialtyFetcher'
import moment from 'moment'
export default {
  components: {
    SlotDial,
    InternshipForm
  },
  mixins: [specialtyFetcher],
  data: function () {
    return {
      isFetching: false,
      dialog: false,
      selectedInternship: null,
      loading: true,
      pagination: {
        sortBy: 'name'
      },
      selected: []
    }
  },
  computed: {
    ...mapGetters(['internships', 'specialty']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    formattedInternships() {
      const allInternships = this.internships.map(internship => {
        let startDate = null
        if (internship.startDate) {
          startDate = moment
            .unix(internship.startDate.seconds)
            .format('DD/MM/YYYY')
        }
        if (startDate === 'Invalid date') {
          startDate = moment(internship.startDate).format('DD/MM/YYYY')
        }
        let endDate = null
        if (internship.endDate) {
          endDate = moment.unix(internship.endDate.seconds).format('DD/MM/YYYY')
        }
        if (endDate === 'Invalid date') {
          endDate = moment(internship.endDate).format('DD/MM/YYYY')
        }
        const period =
          internship.startDate && internship.endDate
            ? `${startDate} - ${endDate}`
            : ''
        const hospital =
          Hospital.query().whereId(internship.hospital).first() || {}
        const hospitalLabel = hospital.text || internship.hospital || ''
        const healthCenterGroup =
          HealthCenterGroup.query().whereId(internship.healthCenter).first() ||
          {}
        const healthCenterGroupLabel = healthCenterGroup.text || ''
        const internshipLabel = this.getInternshipLabel(internship.internship)
        const rotation = this.getRotationLabel(internship.rotation)
        return {
          ...internship,
          period,
          hospital: hospitalLabel,
          healthCenter: healthCenterGroupLabel,
          internship: internshipLabel,
          rotation
        }
      })
      return allInternships
    },
    internshipItems() {
      return Internship.query().get()
    },
    headers() {
      let header = [
        {
          text: this.$t('internship.ResidencyYear'),
          value: 'residencyYear'
        },
        {
          text: this.$t('internship.Internship'),
          value: 'internship'
        },
        {
          text: this.$t('internship.Ward'),
          value: 'ward'
        },
        {
          text: this.$t('internship.Period'),
          value: 'period'
        },
        {
          text: this.$t('internship.Tutor'),
          value: 'tutor'
        },
        {
          text: this.$t('internship.Hospital'),
          value: 'hospital'
        }
      ]
      if (this.specialty === 'family-medicine') {
        header.push({
          text: this.$t('internship.HealthCenterGroup'),
          value: 'healthCenter'
        })
      }
      if (this.specialty === 'general-surgery') {
        header.splice(2, 0, {
          text: this.$t('internship.Rotation'),
          value: 'rotation'
        })
      }
      return header
    }
  },
  async created() {
    this.isFetching = true
    this.fetchSpecialtyIfEmpty()
      .then(() => {
        this.getInternships().finally(() => {
          this.loading = false
          this.isFetching = false
        })
      })
      .catch(err =>
        this.showErrorSnackbar(err.message || 'Something went wrong')
      )
    const hospitals = Hospital.query().get()
    if (!hospitals.length) {
      await Hospital.fetch()
    }
    const healthCenterGroups = HealthCenterGroup.query().get()
    if (!healthCenterGroups.length) {
      await HealthCenterGroup.fetch()
    }
    this.dialog = true
  },
  methods: {
    ...mapActions(['getInternships', 'deleteInternships', 'showErrorSnackbar']),
    deleteEntries() {
      this.loading = 'error'
      this.deleteInternships(this.selected)
        .then(() => {
          this.showErrorSnackbar('Entries Deleted')
        })
        .catch(({ message = 'Something went wrong' }) => {
          this.showErrorSnackbar(message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    closeDialog() {
      this.dialog = false
      this.selectedInternship = null
    },
    toggleAll() {
      if (this.selected.length) this.selected = []
      else this.selected = (this.entries || []).slice()
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
    },
    getInternshipLabel(id) {
      let internship = {}
      const option = Option.query().where('value', id).first()
      if (option) return option.text

      if (this.$store.getters.specialty === 'general-surgery') {
        internship =
          cms.getInternships(this.$i18n.locale).find(i => i.value === id) || {}
      } else {
        const internshipOption =
          this.internshipItems.find(i => i.id === id) || {}
        internship = { text: internshipOption.name }
      }
      return internship.text || id || 'N/A'
    },
    getRotationLabel(id) {
      const rotation =
        cms.getRotations(this.$i18n.locale).find(r => r.value === id) || {}
      return rotation.text || 'N/A'
    },

    editInternship(selectedInternship) {
      this.selectedInternship = this.internships[selectedInternship.index]
      this.dialog = true
    }
  }
}
</script>

<style></style>
